import { useState } from "react";
import {
  Box, Typography, Paper, Button, Stack, Grid2 as Grid, TextField, Alert, MenuItem,
  Table, TableHead, TableRow, TableCell, TableBody, IconButton, Divider, CircularProgress, Chip,
  Tabs, Tab,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import PostAddIcon from "@mui/icons-material/PostAdd";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import LockClockIcon from "@mui/icons-material/LockClock";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  salesInvoicesApi, branchesApi, itemPickerApi, billingOffersApi,
  type ItemDto, type CustomerDto,
  type CreateSalesInvoiceRequest, type CreateSalesInvoiceLineRequest,
  type LineType, type PaymentLineRequest, type SubstituteOptionDto,
} from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { ItemPicker, CustomerPicker } from "@/components/Pickers";
import { SubstitutePanel } from "./SubstitutePanel";

interface DraftLine {
  key: string;
  lineType: LineType;
  item: ItemDto | null;
  description?: string;
  prescriptionRef?: string;
  qty: number;
  rate: number;
  taxAmount: number;
  discountAmount: number;
  substitutedFromItemId?: string;
  substituteReasonCode?: string;
}

interface DraftPayment {
  key: string;
  mode: PaymentLineRequest["mode"];
  amountReceived: number;
  reference?: string;
}

const newLine = (lineType: LineType = "Item"): DraftLine => ({
  key: crypto.randomUUID(), lineType, item: null,
  qty: 1, rate: 0, taxAmount: 0, discountAmount: 0,
});

const newPayment = (mode: PaymentLineRequest["mode"] = "Cash"): DraftPayment => ({
  key: crypto.randomUUID(), mode, amountReceived: 0,
});

/** Compose the description sub-line shown in the cart: form · packaging · strength. */
function describeItem(item: { form?: string; packaging?: string; strength?: string } | null): string {
  if (!item) return "";
  return [item.form, item.packaging, item.strength].filter(Boolean).join(" · ");
}

const LINE_TYPES: LineType[] = ["Rx", "Item", "Service", "Other"];
const PAYMENT_MODES: PaymentLineRequest["mode"][] = ["Cash", "Card", "Upi", "BankTransfer", "Cheque", "Credit"];
const inr = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" });

export function CreateSalesInvoicePage() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [customer, setCustomer] = useState<CustomerDto | null>(null);
  const [customerName, setCustomerName] = useState<string>("");
  const [customerPhone, setCustomerPhone] = useState<string>("");
  const [warehouseId, setWarehouseId] = useState<string>("");
  const [documentDate, setDocumentDate] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [discountAmount, setDiscountAmount] = useState<number>(0);
  const [notes, setNotes] = useState<string>("");
  const [activeTab, setActiveTab] = useState<LineType>("Item");
  const [lines, setLines] = useState<DraftLine[]>([newLine("Item")]);
  const [payments, setPayments] = useState<DraftPayment[]>([newPayment()]);
  const [substituteFor, setSubstituteFor] = useState<{ lineKey: string; item: ItemDto; qty: number } | null>(null);
  const [barcode, setBarcode] = useState<string>("");

  const branches = useQuery({
    queryKey: ["branches"], queryFn: () => branchesApi.list(), staleTime: 5 * 60_000,
  });

  const updateLine = (key: string, patch: Partial<DraftLine>) =>
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const removeLine = (key: string) => setLines((ls) => ls.filter((l) => l.key !== key));
  const addLine = () => setLines((ls) => [...ls, newLine(activeTab)]);

  /** UC-M1-SAL-07: scanner-input box. On Enter, resolve the SKU and append a stock-policy=Issue line. */
  const scanBarcode = async () => {
    const code = barcode.trim();
    if (!code || !warehouseId) return;
    try {
      const row = await itemPickerApi.byBarcode(code);
      // Build an ItemDto compatible with the existing line model.
      const itemLike: ItemDto = {
        id: row.id, sku: row.sku, name: row.name, genericName: row.genericName,
        itemType: "Medicine", baseUomId: "", costingMethod: "FEFO",
        reorderLevel: 0, reorderQty: 0, safetyStock: 0, leadTimeDays: 0,
        isBatchTracked: true, isExpiryTracked: true,
        form: row.form, packaging: row.packaging, strength: row.strength,
        manufacturer: row.manufacturer, category: row.category,
        defaultMrp: row.mrp, gstRate: 0, isScheduleDrug: row.isScheduleDrug,
      };
      setLines((ls) => [
        ...ls.filter(l => !(l.lineType === activeTab && l.item === null)),
        { ...newLine(activeTab), item: itemLike, rate: row.mrp ?? 0 },
      ]);
      setBarcode("");
    } catch (e) {
      // Surface error inline near the scan field — picker already shows toast for 404.
      console.warn("barcode lookup failed", e);
    }
  };

  const updatePayment = (key: string, patch: Partial<DraftPayment>) =>
    setPayments((ps) => ps.map((p) => (p.key === key ? { ...p, ...patch } : p)));
  const removePayment = (key: string) => setPayments((ps) => ps.filter((p) => p.key !== key));
  const addPayment = () => setPayments((ps) => [...ps, newPayment()]);

  const handleSubstituteSelect = (sub: SubstituteOptionDto) => {
    if (!substituteFor) return;
    // Replace the line's item with the substitute
    updateLine(substituteFor.lineKey, {
      // We don't have a full ItemDto for the substitute. Construct a partial one
      // adequate for the picker display. The backend doesn't need our local item state
      // for validation — only the itemId.
      item: {
        id: sub.itemId, sku: sub.sku, name: sub.name, genericName: sub.genericName,
        form: sub.form, strength: sub.strength, manufacturer: sub.manufacturer,
        itemType: "Medicine", baseUomId: "", costingMethod: "WeightedAverage",
        reorderLevel: 0, reorderQty: 0, safetyStock: 0, leadTimeDays: 0,
        isBatchTracked: true, isExpiryTracked: true,
        gstRate: 0, isScheduleDrug: false,
        defaultMrp: sub.mrp,
      } as ItemDto,
      substitutedFromItemId: substituteFor.item.id,
      substituteReasonCode: sub.reasonCode,
      rate: sub.mrp ?? 0,
    });
    setSubstituteFor(null);
  };

  const subTotal = lines.reduce((s, l) => s + l.qty * l.rate, 0);
  const taxTotal = lines.reduce((s, l) => s + l.taxAmount, 0);
  const lineDisc = lines.reduce((s, l) => s + l.discountAmount, 0);
  const netAmount = subTotal + taxTotal - lineDisc - discountAmount;
  const totalReceived = payments.reduce((s, p) => s + p.amountReceived, 0);
  const balanceDue = Math.max(0, netAmount - totalReceived);
  const changeToReturn = Math.max(0, totalReceived - netAmount);

  /** Mockup §UX_WORKFLOWS §4 — one-tap "Fill balance on Cash" for counter speed. */
  const fillBalanceOnCash = () => {
    if (balanceDue <= 0) return;
    setPayments((ps) => {
      const cashIdx = ps.findIndex(p => p.mode === "Cash");
      if (cashIdx >= 0) {
        const next = [...ps];
        next[cashIdx] = { ...next[cashIdx], amountReceived: next[cashIdx].amountReceived + balanceDue };
        return next;
      }
      return [...ps, { ...newPayment("Cash"), amountReceived: balanceDue }];
    });
  };

  /** Rx/Item lines that will be reserved when Submit is called (UC-M1-SAL-06 preview chip). */
  const reservationPreview = lines.filter(l => (l.lineType === "Rx" || l.lineType === "Item") && l.item);

  const validate = (): string | null => {
    if (!warehouseId) return "Pick a warehouse";
    if (lines.length === 0) return "Add at least one line";
    for (const [i, l] of lines.entries()) {
      if (l.lineType !== "Other" && l.lineType !== "Service" && !l.item)
        return `Line ${i + 1}: item not selected`;
      if (l.lineType === "Other" && !l.description) return `Line ${i + 1}: description required`;
      if (l.lineType === "Rx" && !l.prescriptionRef) return `Line ${i + 1}: Rx reference required`;
      if (l.qty <= 0) return `Line ${i + 1}: qty must be > 0`;
      if (l.rate < 0) return `Line ${i + 1}: rate must be >= 0`;
    }
    if (netAmount > 0 && totalReceived === 0 && payments.every(p => p.mode !== "Credit"))
      return "Add at least one payment (or use Credit mode)";
    return null;
  };

  /**
   * S3.1/S3.4 workflow: Create draft → fetch RowVersion → Submit (with If-Match) →
   * Post with payment table. The Submit hop reserves stock and surfaces the
   * BIL-002 422 + substitutesOffered payload when an Rx/Item line is OOS.
   */
  const mutation = useMutation({
    meta: { successMessage: "Sales invoice created" },
    mutationFn: async () => {
      const cmd: CreateSalesInvoiceRequest = {
        customerId: customer?.id ?? undefined,
        customerName: customer ? undefined : (customerName || undefined),
        customerPhone: customer ? undefined : (customerPhone || undefined),
        billType: "OutPatient",
        warehouseId,
        documentDate,
        discountAmount,
        notes: notes || undefined,
        lines: lines.map((l, idx): CreateSalesInvoiceLineRequest => ({
          lineType: l.lineType,
          itemId: l.item?.id,
          description: l.description,
          prescriptionRef: l.prescriptionRef,
          substitutedFromItemId: l.substitutedFromItemId,
          substituteReasonCode: l.substituteReasonCode,
          qty: l.qty, rate: l.rate, taxAmount: l.taxAmount, discountAmount: l.discountAmount,
          lineNo: idx + 1,
        })),
      };
      const created = await salesInvoicesApi.create(cmd);
      const detail = await salesInvoicesApi.get(created.id) as { rowVersion: string };
      await salesInvoicesApi.submit(created.id, detail.rowVersion);

      const paymentPayload: PaymentLineRequest[] = payments
        .filter(p => p.amountReceived > 0 || p.mode === "Credit")
        .map(p => ({ mode: p.mode, amountReceived: p.amountReceived, reference: p.reference || undefined }));
      await salesInvoicesApi.post(created.id, paymentPayload);
      return created;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sales-invoices"] });
      navigate("/sales/invoices");
    },
  });

  const validationError = validate();
  const showSubstitutePanel = !!substituteFor && !!warehouseId;

  return (
    <Box sx={{ display: "flex", gap: 0, height: "calc(100vh - 96px)" }}>
      <Box sx={{ flex: 1, overflow: "auto", pr: showSubstitutePanel ? 2 : 0 }}>
        <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>Outpatient billing</Typography>
        <Paper sx={{ p: 3 }}>
          {mutation.isError && (
            <Alert severity="error" sx={{ mb: 2 }} data-testid="billing-server-error">
              {errorMessage(mutation.error)}
            </Alert>
          )}

          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Patient</Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <CustomerPicker value={customer} onChange={setCustomer} label="Existing customer" />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="caption" color="text.secondary">
                — or fill walk-in details below —
              </Typography>
              <Stack direction="row" spacing={1}>
                <TextField label="Walk-in name" size="small" fullWidth
                  value={customerName} onChange={(e) => setCustomerName(e.target.value)}
                  disabled={!!customer} />
                <TextField label="Walk-in phone" size="small" sx={{ width: 180 }}
                  value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)}
                  disabled={!!customer} />
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField select SelectProps={{ native: true }} InputLabelProps={{ shrink: true }} label="Pharmacy / warehouse *"
                size="small" fullWidth value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
                <option value="">— Pick branch —</option>
                {branches.data?.map((b) => <option key={b.id} value={b.id}>{b.code} — {b.name}</option>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 6, md: 4 }}>
              <TextField label="Bill date *" type="date" size="small" fullWidth
                value={documentDate} onChange={(e) => setDocumentDate(e.target.value)}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid size={{ xs: 6, md: 4 }}>
              <TextField type="number" label="Header discount (₹)" size="small" fullWidth
                value={discountAmount} onChange={(e) => setDiscountAmount(Number(e.target.value) || 0)}
                inputProps={{ min: 0, step: "0.01" }} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField label="Notes" size="small" fullWidth value={notes}
                onChange={(e) => setNotes(e.target.value)} />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* UC-M1-SAL-05 offers / scheme banners — sourced from GET /billing-offers/active. */}
          <BillingOfferBanners />


          {/* Mockup §5 — Rx | Item | Service | Other tabs drive the default line type. */}
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v as LineType)}
            sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
            data-testid="billing-line-type-tabs"
          >
            {LINE_TYPES.map(t => (
              <Tab
                key={t} label={t} value={t}
                sx={{ minHeight: 48, fontWeight: 600 }}
                data-testid={`billing-tab-${t.toLowerCase()}`}
              />
            ))}
          </Tabs>

          {/* UC-M1-SAL-07 — barcode scan input (counter wedge or camera). Enter to add line. */}
          <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="center">
            <QrCodeScannerIcon color="action" />
            <TextField
              size="small"
              placeholder="Scan or type a barcode (GTIN / EAN-13)"
              value={barcode}
              onChange={(e) => setBarcode(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); scanBarcode(); } }}
              sx={{ flex: 1, maxWidth: 360 }}
              disabled={!warehouseId}
              inputProps={{ "data-testid": "billing-barcode-input" }}
            />
            <Button
              onClick={scanBarcode} disabled={!warehouseId || !barcode.trim()} size="small"
              data-testid="billing-barcode-add"
            >
              Add by scan
            </Button>
          </Stack>

          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="subtitle2">Lines</Typography>
              {reservationPreview.length > 0 && (
                <Chip
                  icon={<LockClockIcon />}
                  size="small"
                  color="info"
                  variant="outlined"
                  label={`${reservationPreview.length} line(s) will be reserved on Submit`}
                />
              )}
            </Stack>
            <Button startIcon={<AddIcon />} onClick={addLine} size="small">
              Add {activeTab} line
            </Button>
          </Stack>

          <Table size="small" data-testid="billing-line-cart">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 110 }}>Type *</TableCell>
                <TableCell sx={{ minWidth: 280 }}>Item / Service *</TableCell>
                <TableCell sx={{ width: 130 }}>Rx ref / Desc</TableCell>
                <TableCell align="right" sx={{ width: 80 }}>Qty *</TableCell>
                <TableCell align="right" sx={{ width: 100 }}>Rate (₹)</TableCell>
                <TableCell align="right" sx={{ width: 90 }}>Tax</TableCell>
                <TableCell align="right" sx={{ width: 90 }}>Disc</TableCell>
                <TableCell align="right" sx={{ width: 110 }}>Total</TableCell>
                <TableCell sx={{ width: 80 }} />
              </TableRow>
            </TableHead>
            <TableBody data-testid="billing-line-cart-body">
              {lines.map((l, idx) => {
                const lineTotal = l.qty * l.rate + l.taxAmount - l.discountAmount;
                const needsItem = l.lineType === "Rx" || l.lineType === "Item";
                return (
                  <TableRow
                    key={l.key}
                    data-testid={`billing-line-row-${idx}`}
                    data-line-type={l.lineType}
                  >
                    <TableCell>
                      <TextField select size="small" value={l.lineType}
                        onChange={(e) => updateLine(l.key, { lineType: e.target.value as LineType, item: null, description: "" })}
                        sx={{ width: 100 }}>
                        {LINE_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                      </TextField>
                    </TableCell>
                    <TableCell>
                      {needsItem ? (
                        <Stack spacing={0.25}>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <ItemPicker value={l.item} onChange={(v) => {
                              const r = v?.defaultMrp ?? 0;
                              updateLine(l.key, { item: v, rate: l.rate || r });
                            }} label={`Line ${idx + 1}`} required error={!l.item} />
                            {l.substitutedFromItemId && (
                              <Chip size="small" color="warning" label="Substituted" icon={<SwapHorizIcon />} />
                            )}
                          </Stack>
                          {l.item && describeItem(l.item) && (
                            <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
                              {describeItem(l.item)}
                              {l.substitutedFromItemId && substituteFor?.item && (
                                <> · Substituted for {substituteFor.item.name}</>
                              )}
                            </Typography>
                          )}
                        </Stack>
                      ) : l.lineType === "Service" ? (
                        <TextField placeholder="Service code / pick later" size="small" fullWidth
                          value={l.description ?? ""} onChange={(e) => updateLine(l.key, { description: e.target.value })} />
                      ) : (
                        <TextField placeholder="Free-text description" size="small" fullWidth
                          value={l.description ?? ""} onChange={(e) => updateLine(l.key, { description: e.target.value })} />
                      )}
                    </TableCell>
                    <TableCell>
                      {l.lineType === "Rx" && (
                        <TextField placeholder="Rx #" size="small" value={l.prescriptionRef ?? ""}
                          onChange={(e) => updateLine(l.key, { prescriptionRef: e.target.value })}
                          sx={{ width: 120 }} />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <TextField type="number" size="small" value={l.qty}
                        onChange={(e) => updateLine(l.key, { qty: Number(e.target.value) || 0 })}
                        inputProps={{ min: 0, step: "1", style: { textAlign: "right" }, "data-testid": `billing-line-qty-${idx}` }} sx={{ width: 70 }} />
                    </TableCell>
                    <TableCell align="right">
                      <TextField type="number" size="small" value={l.rate}
                        onChange={(e) => updateLine(l.key, { rate: Number(e.target.value) || 0 })}
                        inputProps={{ min: 0, step: "0.01", style: { textAlign: "right" } }} sx={{ width: 90 }} />
                    </TableCell>
                    <TableCell align="right">
                      <TextField type="number" size="small" value={l.taxAmount}
                        onChange={(e) => updateLine(l.key, { taxAmount: Number(e.target.value) || 0 })}
                        inputProps={{ min: 0, step: "0.01", style: { textAlign: "right" } }} sx={{ width: 80 }} />
                    </TableCell>
                    <TableCell align="right">
                      <TextField type="number" size="small" value={l.discountAmount}
                        onChange={(e) => updateLine(l.key, { discountAmount: Number(e.target.value) || 0 })}
                        inputProps={{ min: 0, step: "0.01", style: { textAlign: "right" } }} sx={{ width: 80 }} />
                    </TableCell>
                    <TableCell align="right">{inr.format(lineTotal)}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        {needsItem && l.item && warehouseId && (
                          <IconButton size="small" color="warning"
                            title="Find substitutes for this item"
                            onClick={() => setSubstituteFor({ lineKey: l.key, item: l.item!, qty: l.qty })}>
                            <SwapHorizIcon fontSize="small" />
                          </IconButton>
                        )}
                        <IconButton size="small" onClick={() => removeLine(l.key)} disabled={lines.length === 1}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          <Divider sx={{ my: 3 }} />
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="subtitle2">Payments</Typography>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="outlined"
                disabled={balanceDue <= 0}
                onClick={fillBalanceOnCash}
              >
                Fill ₹{balanceDue.toFixed(2)} on Cash
              </Button>
              <Button startIcon={<AddIcon />} onClick={() => addPayment()} size="small">Add payment</Button>
            </Stack>
          </Stack>
          <Table size="small" data-testid="billing-payments-table">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 140 }}>Mode</TableCell>
                <TableCell align="right" sx={{ width: 140 }}>Amount (₹)</TableCell>
                <TableCell>Reference</TableCell>
                <TableCell sx={{ width: 60 }} />
              </TableRow>
            </TableHead>
            <TableBody data-testid="billing-payments-body">
              {payments.map((p, pIdx) => (
                <TableRow
                  key={p.key}
                  data-testid={`billing-payment-row-${pIdx}`}
                  data-payment-mode={p.mode}
                >
                  <TableCell>
                    <TextField select size="small" value={p.mode}
                      onChange={(e) => updatePayment(p.key, { mode: e.target.value as PaymentLineRequest["mode"] })}
                      sx={{ width: 130 }}
                      inputProps={{ "data-testid": `billing-payment-mode-${pIdx}` }}>
                      {PAYMENT_MODES.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                    </TextField>
                  </TableCell>
                  <TableCell align="right">
                    <TextField type="number" size="small" value={p.amountReceived}
                      onChange={(e) => updatePayment(p.key, { amountReceived: Number(e.target.value) || 0 })}
                      inputProps={{ min: 0, step: "0.01", style: { textAlign: "right" }, "data-testid": `billing-payment-amount-${pIdx}` }} sx={{ width: 130 }} />
                  </TableCell>
                  <TableCell>
                    <TextField size="small" fullWidth value={p.reference ?? ""}
                      placeholder="Card txn ID, UPI ref, cheque #..."
                      onChange={(e) => updatePayment(p.key, { reference: e.target.value })} />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => removePayment(p.key)} disabled={payments.length === 1}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Divider sx={{ my: 3 }} />
          <Stack direction="row" justifyContent="flex-end" spacing={4} sx={{ mb: 2 }} data-testid="billing-totals">
            <Stack>
              <Typography variant="caption" color="text.secondary">Subtotal</Typography>
              <Typography data-testid="billing-total-subtotal">{inr.format(subTotal)}</Typography>
            </Stack>
            <Stack>
              <Typography variant="caption" color="text.secondary">Tax</Typography>
              <Typography data-testid="billing-total-tax">{inr.format(taxTotal)}</Typography>
            </Stack>
            <Stack>
              <Typography variant="caption" color="text.secondary">Discount</Typography>
              <Typography data-testid="billing-total-discount">− {inr.format(lineDisc + discountAmount)}</Typography>
            </Stack>
            <Stack>
              <Typography variant="caption" color="text.secondary">Net</Typography>
              <Typography variant="h6" color="primary" data-testid="billing-total-net">{inr.format(netAmount)}</Typography>
            </Stack>
            <Stack>
              <Typography variant="caption" color="text.secondary">Received</Typography>
              <Typography data-testid="billing-total-received">{inr.format(totalReceived)}</Typography>
            </Stack>
            <Stack>
              <Typography variant="caption" color="text.secondary">Balance</Typography>
              <Typography variant="h6" color={balanceDue > 0 ? "error" : "success.main"} data-testid="billing-total-balance">
                {inr.format(balanceDue)}
              </Typography>
            </Stack>
            {changeToReturn > 0 && (
              <Stack>
                <Typography variant="caption" color="text.secondary">Change to return</Typography>
                <Chip
                  color="success"
                  label={inr.format(changeToReturn)}
                  sx={{ fontSize: "1rem", fontWeight: 600, height: 32 }}
                  data-testid="billing-total-change"
                />
              </Stack>
            )}
          </Stack>

          <Stack direction="row" justifyContent="flex-end" spacing={2}>
            <Button onClick={() => navigate("/sales/invoices")} data-testid="billing-cancel">Cancel</Button>
            <Button variant="contained" color="success"
              disabled={!!validationError || mutation.isPending}
              startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : <PostAddIcon />}
              onClick={() => mutation.mutate()}
              data-testid="billing-post-invoice">
              Bill & post
            </Button>
          </Stack>
          {validationError && (
            <Alert severity="info" sx={{ mt: 2 }} data-testid="billing-validation-error">
              {validationError}
            </Alert>
          )}
        </Paper>
      </Box>

      {showSubstitutePanel && (
        <Box sx={{ width: 380, flexShrink: 0 }}>
          <SubstitutePanel
            primaryItemId={substituteFor!.item.id}
            primaryItemName={substituteFor!.item.name}
            warehouseId={warehouseId}
            requiredQty={substituteFor!.qty}
            onSelect={handleSubstituteSelect}
            onClose={() => setSubstituteFor(null)}
          />
        </Box>
      )}
    </Box>
  );
}

/**
 * UC-M1-SAL-05 — renders active billing offers as banners on the OP billing form.
 * Fetched once on form open; the OP cashier sees scheme notices but discount application
 * is still per-line manual today (auto-apply per offer scope is a later iteration).
 */
function BillingOfferBanners() {
  const { data, isLoading } = useQuery({
    queryKey: ["billing-offers", "active"],
    queryFn: () => billingOffersApi.active(),
    staleTime: 60_000,
  });

  if (isLoading || !data || data.length === 0) return null;

  return (
    <Stack spacing={1} sx={{ mb: 2 }}>
      {data.map(o => {
        const valueLabel = o.discountType === "Percent"
          ? `${o.discountValue}% off`
          : `₹${o.discountValue} off`;
        const scopeLabel = o.itemName ? ` on ${o.itemName}` : " on eligible items";
        const validity = ` (valid until ${new Date(o.validTo).toLocaleDateString()})`;
        return (
          <Alert
            key={o.id}
            icon={<LocalOfferIcon fontSize="inherit" />}
            severity="info"
          >
            <b>{o.name}</b> — {valueLabel}{scopeLabel}{validity}
            {o.description ? ` · ${o.description}` : ""}
          </Alert>
        );
      })}
    </Stack>
  );
}
